import React, { useMemo } from "react";
import { SimpleGrid, Paper, Text, Group, Stack, Box } from "@mantine/core";
import {
  IconArrowUpRight,
  IconArrowDownRight,
  IconMinus,
} from "@tabler/icons-react";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  Tooltip,
  ReferenceLine,
  XAxis,
} from "recharts";

const TransactionsInsights = ({ daysInMonth, t }) => {
  const stats = useMemo(() => {
    let revenue = 0;
    let expenses = 0;
    let netProfit = 0;
    let activeDays = 0;

    let lastDay = null;
    let prevDay = null;

    daysInMonth.forEach((day) => {
      const p = Number(day.profit) || 0;
      const kp = (Number(day.keitaroProfit) || 0) + (Number(day.fluxarProfit) || 0);
      const totalP = p + kp;
      const sp = (Number(day.spentIo1) || 0) + (Number(day.anotherSpent) || 0);
      const net = totalP - sp;

      revenue += totalP;
      expenses += sp;
      netProfit += net;

      if (sp > 0 || totalP > 0) {
        activeDays++;
        prevDay = lastDay;
        lastDay = { profit: totalP, spent: sp, net: net };
      }
    });

    const roi = expenses > 0 ? (netProfit / expenses) * 100 : 0;

    let trend = { revenue: 0, expenses: 0, netProfit: 0, roi: 0 };
    if (lastDay && prevDay) {
      const calcPerc = (curr, prev) =>
        prev === 0
          ? curr > 0
            ? 100
            : 0
          : ((curr - prev) / Math.abs(prev)) * 100;
      trend.revenue = calcPerc(lastDay.profit, prevDay.profit);
      trend.expenses = calcPerc(lastDay.spent, prevDay.spent);
      trend.netProfit = calcPerc(lastDay.net, prevDay.net);

      const lastRoi =
        lastDay.spent > 0 ? (lastDay.net / lastDay.spent) * 100 : 0;
      const prevRoi =
        prevDay.spent > 0 ? (prevDay.net / prevDay.spent) * 100 : 0;
      trend.roi = lastRoi - prevRoi;
    }

    const chartData = daysInMonth
      .map((day) => {
        const p = Number(day.profit) || 0;
        const kp = (Number(day.keitaroProfit) || 0) + (Number(day.fluxarProfit) || 0);
        const sp = (Number(day.spentIo1) || 0) + (Number(day.anotherSpent) || 0);
        return {
          date: day.date,
          netProfit: p + kp - sp,
        };
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Calculate offset for split gradient (green for positive, red for negative)
    const maxVal = Math.max(...chartData.map((d) => d.netProfit), 0);
    const minVal = Math.min(...chartData.map((d) => d.netProfit), 0);
    let gradientOffset = 0;
    if (maxVal > minVal) {
      gradientOffset = maxVal / (maxVal - minVal);
    } else if (maxVal > 0) {
      gradientOffset = 1;
    }

    return {
      revenue,
      expenses,
      netProfit,
      roi,
      activeDays,
      trend,
      hasTrend: !!(lastDay && prevDay),
      chartData,
      gradientOffset,
    };
  }, [daysInMonth]);


  const renderTrend = (key, isInverse = false) => {
    if (!stats.hasTrend)
      return (
        <Group gap={4} mt="auto">
          <IconMinus size={14} color="#6b7280" />
          <Text size="xs" style={{ color: "#6b7280" }}>
            {t.no_data}
          </Text>
        </Group>
      );

    const trendVal = stats.trend[key];
    const isPositive = trendVal > 0;
    const isZero = trendVal === 0;

    let color = "#6b7280";
    let Icon = IconMinus;

    if (!isZero) {
      Icon = isPositive ? IconArrowUpRight : IconArrowDownRight;
      if (isInverse) {
        color = isPositive ? "#ef4444" : "#10b981"; // Витрати: ріст - червоний, падіння - зелений
      } else {
        color = isPositive ? "#10b981" : "#ef4444"; // Дохід/Профіт: ріст - зелений, падіння - червоний
      }
    }

    return (
      <Group gap={4} mt="auto" align="center">
        <Icon size={16} color={color} stroke={3} />
        <Text size="xs" fw={500} style={{ color }}>
          {Math.abs(trendVal).toFixed(1)}%{" "}
          <span style={{ color: "#6b7280", fontWeight: 400 }}>
            {t.vs_prev_day}
          </span>
        </Text>
      </Group>
    );
  };

  const cards = [
    {
      title: t.revenue,
      value: `$${stats.revenue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      color: "#10b981", // Emerald
      trendKey: "revenue",
      isInverse: false,
    },
    {
      title: t.expenses,
      value: `$${stats.expenses.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      color: "#ef4444", // Red
      trendKey: "expenses",
      isInverse: true,
    },
    {
      title: t.net_profit,
      value: `$${stats.netProfit.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      color: "#10b981", // Emerald
      trendKey: "netProfit",
      isInverse: false,
    },
    {
      title: t.roi,
      value: `${stats.roi.toFixed(1)}%`,
      color: "#8b5cf6", // Purple
      trendKey: "roi",
      isInverse: false,
    },
  ];

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 2, xs: 2, md: 4 }} spacing="md">
        {cards.map((card, idx) => (
          <Paper
            key={idx}
            p="md"
            radius="md"
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              display: "flex",
              flexDirection: "column",
              boxShadow: "none",
            }}
          >
            <Text
              size="xs"
              fw={500}
              style={{ color: "#6b7280" }}
              tt="none"
              ls={0}
            >
              {card.title}
            </Text>
            <Text
              fw={500}
              mt={8}
              mb={12}
              style={{
                color: "#292932",
                fontSize: "24px",
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
              }}
            >
              {card.value}
            </Text>
            {renderTrend(card.trendKey, card.isInverse)}
          </Paper>
        ))}
      </SimpleGrid>

      <Paper
        withBorder
        radius="md"
        p="xs"
        style={{
          backgroundColor: "#ffffff",
          borderColor: "#e5e7eb",
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        <Box h={80}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stats.chartData}>
              <defs>
                <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset={stats.gradientOffset}
                    stopColor="#10b981"
                    stopOpacity={0.3}
                  />
                  <stop
                    offset={stats.gradientOffset}
                    stopColor="#ef4444"
                    stopOpacity={0.3}
                  />
                </linearGradient>
              </defs>
              <Tooltip
                labelFormatter={(v) => {
                  if (!v) return "";
                  const d = new Date(v);
                  return isNaN(d.getTime())
                    ? v
                    : d.toLocaleDateString("uk-UA", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      });
                }}
                contentStyle={{
                  borderRadius: "8px",
                  fontSize: "11px",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              />
              <XAxis dataKey="date" hide />
              <ReferenceLine y={0} stroke="#e5e7eb" strokeDasharray="3 3" />
              <Area
                type="monotone"
                dataKey="netProfit"
                stroke="#82818c"
                fillOpacity={1}
                fill="url(#colorNet)"
                strokeWidth={1.5}
                isAnimationActive={true}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    </Stack>
  );
};

export default TransactionsInsights;
